import { motion } from 'framer-motion';
import { GraduationCap, Target, BookOpen, TrendingUp, ArrowRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../context/AuthContext';

const benefits = [
  {
    icon: Target,
    title: 'Know Exactly Where You\'re Weak',
    description: 'Get precise diagnosis of conceptual gaps, not vague advice.'
  },
  {
    icon: BookOpen,
    title: 'Smart Mistake Notebook',
    description: 'Your personal vault of errors and skills. We turn your wrong answers into daily, personalized revision quizzes so you never make the same mistake twice.'
  },
  {
    icon: TrendingUp,
    title: 'Track Real Progress',
    description: 'See improvement across topics, not just overall scores.'
  }
];

export default function ForStudentsSection() {
  const { login } = useAuth();

  return (
    <section 
      id="for-students" 
      data-testid="for-students-section"
      className="relative py-20 md:py-32 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {/* STATIC BADGE */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6">
              <GraduationCap className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium text-primary">For Students</span>
            </div>

            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-6">
              Stop guessing.
              <br />
              <span className="text-primary">Start understanding.</span>
            </h2>

            <p className="text-lg text-muted-foreground leading-relaxed mb-8">
              Every mark lost has a reason. Edalyze helps you find that reason, fix it, and prevent it from happening again. No more generic feedback — get precise, actionable insights.
            </p>

            {/* STATIC BENEFITS */}
            <div className="space-y-4 mb-8">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-start gap-4 p-4 rounded-xl bg-card border border-border transition-colors"
                >
                  <div className="p-2 rounded-lg bg-primary/10">
                    <benefit.icon className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">{benefit.title}</h4>
                    <p className="text-sm text-muted-foreground">{benefit.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            <a href="https://edalyze.app" target="_blank" rel="noopener noreferrer">
              <Button
                size="lg"
                data-testid="student-cta-btn"
                className="bg-primary text-primary-foreground shadow-lg"
              >
                Try Edalyze
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </a>
          </motion.div>

          {/* Right Visual */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative"
          >
            <div className="relative rounded-2xl overflow-hidden max-w-md mx-auto lg:max-w-none border border-border">
              <img
                src="https://images.unsplash.com/photo-1741699428553-41c8e5bd894d?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                alt="Student studying"
                className="w-full h-auto rounded-2xl"
              />
              
              {/* STATIC OVERLAY CARD */}
              <div className="absolute bottom-2 md:bottom-4 left-2 right-2 md:left-4 md:right-4 glass rounded-xl p-3 md:p-4 border border-white/10">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs md:text-sm text-muted-foreground">Average Improvement</p>
                    <p className="text-xl md:text-2xl font-bold text-primary">+18%</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs md:text-sm text-muted-foreground">Error Reduction</p>
                    <p className="text-xl md:text-2xl font-bold">65%</p>
                  </div>
                </div>
              </div>
            </div>

            {/* STATIC FLOATING BADGE */}
            <div className="hidden sm:block absolute -top-4 -right-4 glass rounded-xl p-3 border border-primary/20 shadow-xl">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-primary" />
                <span className="text-sm font-medium">+21% in 2 months</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
